import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { UsersSelectPaginated } from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import { BaseModalProps, Button, ModalContentWrapper, SelectPaginateOption, TextInput } from 'ui';
import { CreateVaultRequest } from 'compliance-api';

interface CreateVaultModalProps extends BaseModalProps {
  onCreate: (payload: CreateVaultRequest) => Promise<void>;
}

const CreateVaultModal = ({ open, onClose, onCreate }: CreateVaultModalProps) => {
  const { data: currentUser } = useCurrentUserQuery();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sharedUsers, setSharedUsers] = useState<SelectPaginateOption<number>[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetAndClose = () => {
    setName('');
    setDescription('');
    setSharedUsers([]);
    onClose();
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetAndClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Vault name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim() || undefined,
        shared_user_ids: sharedUsers.map(user => user.value),
      });
      resetAndClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalContentWrapper
      open={open}
      title="Create Vault"
      onClose={handleClose}
      variant="center"
      classes={{ dialogPanel: 'max-w-xl' }}
    >
      <div className="px-6 pb-6 pt-2 space-y-4">
        <TextInput
          name="vault_name"
          label="Vault name"
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <TextInput
          name="vault_description"
          label="Description"
          textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          className="min-h-[90px]"
        />
        <UsersSelectPaginated
          isMulti
          label="Shared users"
          placeholder="Select users"
          value={sharedUsers}
          onChange={options => setSharedUsers(options || [])}
          isClearable
          excludeUserIds={currentUser?.user.id ? [currentUser.user.id] : []}
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create vault'}
          </Button>
        </div>
      </div>
    </ModalContentWrapper>
  );
};

export default CreateVaultModal;
